import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Custodian } from '../../../../models/review/custodian.model';
import { CustodianService } from '../../../../services/custodian.service';
import { PatchDeltaObjectUtility } from '../../../../utilities/patch-delta-object.utility';
import { ExportSdk } from '../../../../sdks/export.sdk';
import { ImportSdk } from '../../../../sdks/import.sdk';
import { NotificationManager } from '../../../../managers/notification.manager';


@Component({
    selector: 'lex-custodian-details',
    templateUrl: './custodian-details.component.html',
    styleUrls: ['./custodian-details.component.scss'],
    standalone: false
})
export class CustodianDetailsComponent implements OnInit, OnChanges {

	@Input() custodian: Custodian;
	@Input() showSelect: boolean;
	@Input() canEdit: boolean;
	@Output() updateCustodiansEvent = new EventEmitter();

	@ViewChild('fileUpload', { static: false }) fileInputElement: ElementRef;

	custodianDetailsForm: UntypedFormGroup;
	affiliations: string[] = [];
	filteredAffiliations: Observable<string[]>;

	constructor(
		private formBuilder: UntypedFormBuilder,
		private custodianService: CustodianService,
		private snackBar: MatSnackBar,
		private notificationManager: NotificationManager,
		private exportSdk: ExportSdk,
		private importSdk: ImportSdk) { }

	ngOnInit(): void {
		if (this.custodian) {
			this.onChangeCustodian();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && this.custodian) {
			this.onChangeCustodian();
		}
	}

	onChangeCustodian(): void {
		this.custodianDetailsForm = this.formBuilder.group({
			name: [this.custodian.name, [Validators.required, Validators.maxLength(256)]],
			isOrganization: [{ value: this.custodian.isOrganization, disabled: !this.canEdit }, Validators.required],
			title: [this.custodian.title, [Validators.maxLength(50)]],
			memo: [this.custodian.memo, Validators.maxLength(1024)],
			role: [this.custodian.role, Validators.maxLength(1024)],
			address: [this.custodian.address, Validators.maxLength(256)],
			phone: [this.custodian.phone, [Validators.maxLength(50)]],
			fax: [this.custodian.fax, [Validators.maxLength(50)]],
			cell: [this.custodian.cell, [Validators.maxLength(50)]],
			email: [this.custodian.email, [Validators.email, Validators.maxLength(50)]],
			affiliation: [this.custodian.affiliation, Validators.maxLength(256)],
			customField1: [this.custodian.customField1, Validators.maxLength(2048)]
		});

		//get affiliation list needed by Affiliation autocomplete input field
		this.getAffiliationList();
	}

	onExportCustodians(): void {
		this.exportSdk.exportCustodians().subscribe((url) => {
			window.open(url, '_blank');
		});	
	}

	onFileSelected(event): void {
		const file: File = event.target.files[0];
		if (file) {
			const formData = new FormData();
			formData.append('File', file, file.name);
			formData.append('FileType', file.name.substr(file.name.lastIndexOf('.')));

			this.importSdk.importCustodian(formData).subscribe(() => {
				this.fileInputElement.nativeElement.value = '';
				if (this.custodian)
					this.updateCustodiansEvent.emit(this.custodian.custodianId);
				else
					this.updateCustodiansEvent.emit();
				this.snackBar.open('Custodians imported successfully.', 'X', {
					duration: 3000
				});
			},
				() => {
					this.fileInputElement.nativeElement.value = '';
					this.notificationManager.displaySnackBarMessage({ message: 'Import Custodians encountered an error', type: 'error' });
				});
		}
		else {
			this.fileInputElement.nativeElement.value = '';
		}
	}

	onUpdateCustodian(): void {
		if (!this.custodianDetailsForm.valid)
			return;

		const jobPatch = PatchDeltaObjectUtility.getDeltaObject<Custodian>(this.custodianDetailsForm.controls);

		if (jobPatch === null)
			return;

		this.custodianService.updateCustodian(this.custodian.custodianId, jobPatch).subscribe(() => {
			const keyValueArray = Object.entries(jobPatch);
			keyValueArray.forEach(kvp => {
				this.custodian[kvp[0]] = kvp[1];
			});
			this.onChangeCustodian();
			this.snackBar.open('Custodians updated successfully.', 'X', {
				duration: 3000
			});
		});
	}

	onUndo(): void {
		this.onChangeCustodian();
	}

	private filter(value: string): string[] {
		const filterValue = value.toLowerCase();
		return this.affiliations.filter(option => option.toLowerCase().includes(filterValue));
	}

	private getAffiliationList(): void {
		if (this.custodian) {
			this.custodianService.getCustodianAffiliation().subscribe(data => {
				this.affiliations = data;
				this.filteredAffiliations = this.custodianDetailsForm.get('affiliation').valueChanges
					.pipe(
						startWith(''),
						map(value => this.filter(value))
					);
			});
		}
	}
}
